import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  Download,
  Calendar,
  Clock,
  FileText,
  Mail,
  Plus,
  Trash2,
  Play,
  Pause,
  Loader2,
  CheckCircle2,
  X,
  RefreshCw,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';

interface ScheduledReport {
  id: string;
  name: string;
  reportType: string;
  frequency: string;
  recipients: string[];
  nextRunAt: string;
  lastRunAt: string | null;
  isActive: boolean;
  createdAt: string;
}

interface ReportExport {
  id: string;
  reportType: string;
  format: string;
  status: string;
  downloadUrl: string | null;
  createdAt: string;
  completedAt: string | null;
}

const REPORT_TYPES = [
  { value: 'SUMMARY', label: 'Resumo Geral' },
  { value: 'ANALYST_PRODUCTIVITY', label: 'Produtividade de Analistas' },
  { value: 'SLA_COMPLIANCE', label: 'Cumprimento de SLA' },
  { value: 'REJECTION_ANALYSIS', label: 'Análise de Rejeições' },
  { value: 'DOCUMENT_EXPIRATION', label: 'Expiração de Documentos' },
  { value: 'DETAILED', label: 'Relatório Detalhado' },
];

const FREQUENCIES = [
  { value: 'DAILY', label: 'Diário' },
  { value: 'WEEKLY', label: 'Semanal' },
  { value: 'MONTHLY', label: 'Mensal' },
];

const FORMATS = [
  { value: 'PDF', label: 'PDF' },
  { value: 'EXCEL', label: 'Excel' },
  { value: 'CSV', label: 'CSV' },
];

export function KycReports() {
  const [activeTab, setActiveTab] = useState<'export' | 'scheduled'>('export');
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const tabs = [
    { id: 'export' as const, label: 'Exportar Relatório', icon: Download },
    { id: 'scheduled' as const, label: 'Relatórios Agendados', icon: Calendar },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-primary-400" />
            Relatórios KYC
          </h1>
          <p className="text-surface-400 mt-1">
            Exporte relatórios e configure envios automáticos
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-700 pb-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-colors",
                activeTab === tab.id
                  ? "bg-surface-800 text-primary-400 border-b-2 border-primary-400"
                  : "text-surface-400 hover:text-surface-100"
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="glass rounded-xl p-6">
        {activeTab === 'export' && <ExportTab />}
        {activeTab === 'scheduled' && (
          <ScheduledTab onOpenModal={() => setIsScheduleModalOpen(true)} />
        )}
      </div>

      {/* Schedule Modal */}
      {isScheduleModalOpen && (
        <ScheduleModal
          onClose={() => setIsScheduleModalOpen(false)}
          onSuccess={() => {
            setIsScheduleModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['kyc-scheduled-reports'] });
          }}
        />
      )}
    </div>
  );
}

function ExportTab() {
  const [reportType, setReportType] = useState('SUMMARY');
  const [format, setFormat] = useState('PDF');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  const { data: recentExports, isLoading: loadingExports } = useQuery<ReportExport[]>({
    queryKey: ['kyc-recent-exports'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/reports/exports?limit=10');
      return response.data;
    },
  });

  const exportMutation = useMutation({
    mutationFn: async (data: { reportType: string; format: string; startDate: string; endDate: string }) => {
      const response = await api.post('/admin/kyc/reports/export', data);
      return response.data;
    },
  });

  const handleExport = () => {
    exportMutation.mutate({
      reportType,
      format,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="w-48">
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Tipo de Relatório
          </label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          >
            {REPORT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div className="w-36">
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Formato
          </label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          >
            {FORMATS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        <div className="w-40">
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Data Início
          </label>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          />
        </div>

        <div className="w-40">
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Data Fim
          </label>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          />
        </div>
      </div>

      <button
        onClick={handleExport}
        disabled={exportMutation.isPending}
        className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
      >
        {exportMutation.isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        Gerar Relatório
      </button>

      {exportMutation.isSuccess && (
        <div className="flex items-center gap-2 text-sm text-green-400">
          <CheckCircle2 className="w-4 h-4" />
          Relatório gerado com sucesso! O download iniciará automaticamente.
        </div>
      )}

      {/* Recent Exports */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-surface-100 mb-4">Exportações Recentes</h3>
        {loadingExports ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
          </div>
        ) : recentExports && recentExports.length > 0 ? (
          <div className="space-y-2">
            {recentExports.map((exp) => (
              <div
                key={exp.id}
                className="flex items-center justify-between p-3 bg-surface-800 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-surface-400" />
                  <div>
                    <p className="text-sm font-medium text-surface-100">
                      {REPORT_TYPES.find(t => t.value === exp.reportType)?.label || exp.reportType}
                    </p>
                    <p className="text-xs text-surface-500">
                      {new Date(exp.createdAt).toLocaleString('pt-BR')} • {exp.format}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "px-2 py-1 rounded text-xs font-medium",
                    exp.status === 'COMPLETED' ? "bg-green-500/20 text-green-400" :
                    exp.status === 'PROCESSING' ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-red-500/20 text-red-400"
                  )}>
                    {exp.status === 'COMPLETED' ? 'Concluído' :
                     exp.status === 'PROCESSING' ? 'Processando' : 'Erro'}
                  </span>
                  {exp.downloadUrl && (
                    <a
                      href={exp.downloadUrl}
                      className="p-1.5 text-primary-400 hover:text-primary-300 transition-colors"
                      download
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-surface-500 text-center py-8">Nenhuma exportação recente</p>
        )}
      </div>
    </div>
  );
}

function ScheduledTab({ onOpenModal }: { onOpenModal: () => void }) {
  const queryClient = useQueryClient();

  const { data: scheduledReports, isLoading } = useQuery<ScheduledReport[]>({
    queryKey: ['kyc-scheduled-reports'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/reports/scheduled');
      return response.data;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await api.patch(`/admin/kyc/reports/scheduled/${id}`, { isActive });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-scheduled-reports'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/kyc/reports/scheduled/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-scheduled-reports'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-surface-100">Relatórios Agendados</h3>
        <button
          onClick={onOpenModal}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Novo Agendamento
        </button>
      </div>

      {scheduledReports && scheduledReports.length > 0 ? (
        <div className="space-y-3">
          {scheduledReports.map((report) => (
            <div
              key={report.id}
              className={cn(
                "p-4 rounded-lg border transition-colors",
                report.isActive
                  ? "bg-surface-800 border-surface-700"
                  : "bg-surface-900 border-surface-800 opacity-60"
              )}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-surface-100">{report.name}</h4>
                  <p className="text-sm text-surface-400 mt-1">
                    {REPORT_TYPES.find(t => t.value === report.reportType)?.label || report.reportType}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-surface-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {FREQUENCIES.find(f => f.value === report.frequency)?.label || report.frequency}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {report.recipients.length} destinatário(s)
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Próximo: {new Date(report.nextRunAt).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleMutation.mutate({ id: report.id, isActive: !report.isActive })}
                    className={cn(
                      "p-2 rounded-lg transition-colors",
                      report.isActive
                        ? "text-green-400 hover:bg-green-500/10"
                        : "text-surface-400 hover:bg-surface-700"
                    )}
                    title={report.isActive ? 'Pausar' : 'Ativar'}
                  >
                    {report.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(report.id)}
                    className="p-2 text-surface-400 hover:text-red-400 hover:bg-surface-700 rounded-lg transition-colors"
                    title="Excluir"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-surface-500">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Nenhum relatório agendado</p>
          <p className="text-sm mt-1">Crie um agendamento para receber relatórios automaticamente</p>
        </div>
      )}
    </div>
  );
}

function ScheduleModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    reportType: 'SUMMARY',
    frequency: 'WEEKLY',
    recipients: '',
  });

  const mutation = useMutation({
    mutationFn: async (data: { name: string; reportType: string; frequency: string; recipients: string[] }) => {
      const response = await api.post('/admin/kyc/reports/scheduled', data);
      return response.data;
    },
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const recipients = formData.recipients
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);
    mutation.mutate({ ...formData, recipients });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-surface-900 rounded-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-4 border-b border-surface-700">
          <h2 className="text-lg font-semibold text-surface-100">
            Novo Agendamento de Relatório
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-surface-400 hover:text-surface-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Nome do Agendamento
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
              placeholder="Relatório Semanal de KYC"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Tipo de Relatório
              </label>
              <select
                value={formData.reportType}
                onChange={(e) => setFormData({ ...formData, reportType: e.target.value })}
                className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
              >
                {REPORT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Frequência
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
              >
                {FREQUENCIES.map((freq) => (
                  <option key={freq.value} value={freq.value}>{freq.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Destinatários (e-mails separados por vírgula)
            </label>
            <textarea
              value={formData.recipients}
              onChange={(e) => setFormData({ ...formData, recipients: e.target.value })}
              className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500 min-h-[80px]"
              placeholder="admin@empresa.com, compliance@empresa.com"
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-surface-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-surface-400 hover:text-surface-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              Criar Agendamento
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default KycReports;
